import { useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useShowAddonsShop } from '@app/hooks';
import { AddOnType, selectAddOns, selectHasFiber, selectUserInfo } from '@app/redux';

import { ADDON_KIND, ADDON_PRODUCT_KIND, VOICE_ADDONS_KINDS } from '@mfe/shared/constants';
import { AccountType, GetCurrentAddOnsPayload } from '@mfe/shared/schema-types';

import { useSetAddOnConsent } from '../../../ShopAddons/hooks';
import { AddOnCard } from './AddOnCard';

import styles from './AddOnSection.module.scss';

export const AddOnSection = (): JSX.Element => {
  const { t } = useTranslation('Overview');
  const { loading, currentAddOns, availableAddons } = useSelector(selectAddOns);
  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);
  const hasFiberInternet = useSelector(selectHasFiber);
  const showAddonsShop = useShowAddonsShop();

  const isSMBAccount = accountType === AccountType.Smb;

  const offers = showAddonsShop ? availableAddons.productTypes : null;
  const shouldFilterByOffers = !!offers;

  useSetAddOnConsent(offers);

  const findOffer = (kind: string) => offers?.find((offer) => offer.kind === kind);

  const voiceOffer = findOffer(ADDON_PRODUCT_KIND.voice);
  const hasVoice = currentAddOns?.find((addOn: GetCurrentAddOnsPayload) => VOICE_ADDONS_KINDS.includes(addOn.kind));
  const showVoiceAd = !hasVoice && !hasFiberInternet && (!shouldFilterByOffers || !!voiceOffer);

  const easyCareOffer = findOffer(ADDON_PRODUCT_KIND.easyCare);
  const hasEasyCare = currentAddOns?.find((addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.EASY_CARE);
  const showEasyCareAd =
    !hasEasyCare && !isSMBAccount && !hasFiberInternet && (!shouldFilterByOffers || !!easyCareOffer);

  const staticIPOffer = findOffer(ADDON_PRODUCT_KIND.staticIp);
  const hasStaticIP = currentAddOns?.find((addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.STATIC_IP);
  const showStaticIPAd = !hasStaticIP && isSMBAccount && (!shouldFilterByOffers || !!staticIPOffer);

  const cyberProtectOffer = findOffer(ADDON_PRODUCT_KIND.cyberProtect);
  const hasCyberProtect = currentAddOns?.find(
    (addOn: GetCurrentAddOnsPayload) => addOn.kind === ADDON_KIND.CYBER_PROTECT
  );
  const showCyberProtectAd = !hasCyberProtect && !isSMBAccount && (!shouldFilterByOffers || !!cyberProtectOffer);

  const getMoreText = t('getMore');
  const getMoreSection = (
    <div className={styles['addOnText']}>
      <Txt variant="heading5" component="span">
        {getMoreText}
      </Txt>
    </div>
  );

  const isLoading = loading || availableAddons.loading;
  const addOnCards = [
    { type: AddOnType.Voice, offer: voiceOffer, shouldShowCard: showVoiceAd },
    { type: AddOnType.EasyCare, offer: easyCareOffer, shouldShowCard: showEasyCareAd },
    { type: AddOnType.CyberProtect, offer: cyberProtectOffer, shouldShowCard: showCyberProtectAd },
    { type: AddOnType.StaticIP, offer: staticIPOffer, shouldShowCard: showStaticIPAd },
  ];

  return (
    <div className={styles['addOnSection']}>
      {getMoreSection}
      <div className={styles['addOnCardList']}>
        {addOnCards.map(
          ({ type, offer, shouldShowCard }) =>
            shouldShowCard && <AddOnCard key={type} loading={isLoading} type={type} offer={offer} />
        )}
      </div>
    </div>
  );
};

export default AddOnSection;
